import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Modal, Form, Divider, Typography, Space, Alert, Row, Col, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { isScreeningRelatedTemplate, TRIGGER_TEMPLATES } from '../../constants/alkymation';
import { t } from '../../i18n/i18n';
import ModalStrategyFactory from './strategies/ModalStrategyFactory';
import NodeInputPanel from './NodeInputPanel';
import NodeOutputPanel from './NodeOutputPanel';
import './NodeConfigModal.scss';

const { Text } = Typography;

const NodeConfigModal = ({ sandbox }) => {
  const [form] = Form.useForm();
  const [screenings, setScreenings] = useState([]);
  const [loadingScreenings, setLoadingScreenings] = useState(false);
  const [selectedScreeningId, setSelectedScreeningId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedNode = sandbox?.getSelectedNode();

  const previousNode = useMemo(() => {
    if (!selectedNode) return null;

    const incomingConnection = sandbox?.workflow?.connections?.find(
      (conn) => conn?.targetNodeId === selectedNode?.id
    );

    if (!incomingConnection) return null;

    return sandbox?.workflow?.nodes?.find((node) => node?.id === incomingConnection?.sourceNodeId);
  }, [selectedNode, sandbox?.workflow?.connections, sandbox?.workflow?.nodes]);

  // TODO: In the future, get real execution data from the previous node
  // For now, we'll show the template outputs as example data
  const previousNodeOutputData = useMemo(() => {
    if (!previousNode || !previousNode?.template?.outputs) return null;

    // Simulate example output data based on template
    const exampleData = {};
    previousNode?.template?.outputs?.forEach((output) => {
      // Create example values based on type
      if (output?.type === 'string') {
        exampleData[output?.id] = 'example_value';
      } else if (output?.type === 'number') {
        exampleData[output?.id] = 123;
      } else if (output?.type === 'boolean') {
        exampleData[output?.id] = true;
      } else if (output?.type === 'object') {
        exampleData[output?.id] = { key: 'value' };
      } else if (output?.type === 'array') {
        exampleData[output?.id] = ['item1', 'item2'];
      } else {
        exampleData[output?.id] = null;
      }
    });

    // Add specific data for webhook-screening-complete
    const isPreviousNodeScreeningWebhook =
      previousNode?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE ||
      previousNode?.template?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE;

    if (isPreviousNodeScreeningWebhook) {
      return { screeningScore: t('SCREENING_DATA_EXAMPLE_SCORE') };
    }

    return exampleData;
  }, [previousNode]);

  const strategy = useMemo(() => {
    if (!selectedNode) return null;
    const modalStrategy = ModalStrategyFactory?.getStrategy(selectedNode);
    modalStrategy?.initialize({
      form,
      selectedNode,
      sandbox,
      previousNode,
      previousNodeOutputData,
    });
    return modalStrategy;
  }, [selectedNode, form, sandbox, previousNode, previousNodeOutputData]);

  useEffect(() => {
    if (selectedNode && sandbox?.isConfigModalOpen) {
      const processedParameters = {};

      Object.keys(selectedNode?.parameters || {}).forEach((key) => {
        const paramDef = selectedNode?.template?.parameters?.find((p) => p?.id === key);
        const value = selectedNode?.parameters?.[key];

        if (paramDef && paramDef?.type === 'date') {
          if (value) {
            try {
              let momentDate;
              if (typeof value === 'string' && value?.trim() !== '') {
                momentDate = moment(value);
              } else if (moment.isMoment(value)) {
                momentDate = value;
              } else if (value instanceof Date) {
                momentDate = moment(value);
              }

              if (momentDate && momentDate?.isValid()) {
                processedParameters[key] = momentDate;
              }
            } catch (error) {
              console.error('Error converting date:', error);
            }
          }
        } else {
          processedParameters[key] = value;
        }
      });

      form?.resetFields();
      form?.setFieldsValue(processedParameters);

      if (selectedNode?.parameters?.screeningId) {
        setSelectedScreeningId(selectedNode?.parameters?.screeningId);
      } else {
        setSelectedScreeningId(null);
      }

      if (isScreeningRelatedTemplate({ node: selectedNode }) && strategy) {
        strategy?.initializeWithHooks({
          setScreenings,
          setLoadingScreenings,
          setSelectedScreeningId,
          setCurrentPage,
          setSearchTerm,
        });
        strategy?.loadScreenings();
      }
    }
  }, [selectedNode, sandbox?.isConfigModalOpen, form, strategy]);

  const handleSave = async () => {
    try {
      // Validación manual para screening si es un template relacionado
      if (isScreeningRelatedTemplate({ node: selectedNode })) {
        const screeningId = form?.getFieldValue('screeningId');
        if (!screeningId) {
          notification?.warning({
            message: t('ALKYMATION_MUST_SELECT_SCREENING'),
            placement: 'topRight',
          });
          return;
        }
      }

      const values = await form?.validateFields();

      const processedValues = { ...values };
      if (selectedNode?.template && selectedNode?.template?.parameters) {
        selectedNode?.template?.parameters?.forEach((param) => {
          if (param?.type === 'date' && processedValues?.[param?.id]) {
            if (
              moment.isMoment(processedValues?.[param?.id]) &&
              processedValues?.[param?.id]?.isValid()
            ) {
              processedValues[param?.id] = processedValues?.[param?.id]?.toISOString();
            }
          }
        });
      }

      sandbox?.updateNodeParameters(selectedNode?.id, processedValues);
      sandbox?.setIsConfigModalOpen(false);
    } catch (error) {
      console.error(error?.message);
    }
  };

  const handleCancel = () => {
    sandbox?.setIsConfigModalOpen(false);
    form?.resetFields();
  };

  const renderNodeStats = () => {
    if (!selectedNode) return null;

    return (
      <div className="node-stats mt-3">
        <Space split={<Divider type="vertical" />}>
          <Text type="secondary">
            <strong>{t('ALKYMATION_INPUTS')}:</strong> {selectedNode?.template?.maxInputs}
          </Text>
          <Text type="secondary">
            <strong>{t('ALKYMATION_OUTPUTS')}:</strong> {selectedNode?.template?.outputs?.length}
          </Text>
          <Text type="secondary">
            <strong>{t('ALKYMATION_ID')}:</strong>{' '}
            {selectedNode?.id?.split('_')?.[1]?.substring(0, 8)}
            ...
          </Text>
        </Space>
      </div>
    );
  };

  if (!selectedNode || !strategy) return null;

  return (
    <Modal
      title={
        <div className="modal-header">
          <Space>
            <img
              className="node-icon"
              src={`data:image/svg+xml;base64,${btoa(selectedNode?.template?.icon || '')}`}
              style={{
                backgroundColor: selectedNode?.template?.color,
                padding: '4px 8px',
                borderRadius: '4px',
                color: 'white',
              }}
              alt=""
            />
            <div>
              <div>
                {t('ALKYMATION_CONFIGURE_NODE')}: {t(selectedNode?.name)}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t(selectedNode?.template?.category)}
              </Text>
            </div>
          </Space>
        </div>
      }
      open={sandbox?.isConfigModalOpen}
      onOk={handleSave}
      onCancel={handleCancel}
      width="95vw"
      okText={t('ALKYMATION_SAVE_CONFIGURATION')}
      cancelText={t('ALKYMATION_CANCEL')}
      className="node-config-modal three-column-layout"
      bodyStyle={{
        padding: 0,
        height: '80vh',
      }}
    >
      <Row className="modal-content-row" style={{ height: '100%' }}>
        {/* Left Panel - Input Data from Previous Node */}
        <Col span={6} className="left-panel-col">
          <NodeInputPanel
            previousNode={previousNode}
            previousNodeOutputData={previousNodeOutputData}
          />
        </Col>

        {/* Center Panel - Node Configuration */}
        <Col span={12} className="center-panel-col">
          <div className="center-panel-content">
            <Alert
              message={t(selectedNode?.template?.description)}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              className="mb-3"
            />

            <Form form={form} layout="vertical">
              {strategy?.renderBody({
                screenings,
                loadingScreenings,
                selectedScreeningId,
                templateId: selectedNode?.templateId,
                currentPage,
                searchTerm,
              })}
            </Form>

            {renderNodeStats()}
          </div>
        </Col>

        {/* Right Panel - Output Data */}
        <Col span={6} className="right-panel-col">
          <NodeOutputPanel currentNode={selectedNode} />
        </Col>
      </Row>
    </Modal>
  );
};

export default NodeConfigModal;
